import type { EnemyClass } from '../entities/EnemyUnit'

export type MissionPhase = 'active' | 'intermission' | 'complete'

export interface MissionUnitDefinition {
  readonly id: string
  readonly enemyClass: EnemyClass
  readonly position: readonly [number, number, number]
  readonly phaseOffset: number
}

export interface MissionWaveDefinition {
  readonly id: string
  readonly title: string
  readonly detail: string
  readonly units: readonly MissionUnitDefinition[]
}

export interface MissionStateSnapshot {
  phase: MissionPhase
  stage: number
  totalStages: number
  waveEnemiesAlive: number
  intermissionRemaining: number
  progress: number
}

export interface MissionTransition {
  enteredIntermission: boolean
  completed: boolean
  spawnWave: MissionWaveDefinition | null
}

export const DEFAULT_MISSION_WAVES: readonly MissionWaveDefinition[] = [
  {
    id: 'screening-line',
    title: 'BREACH THE SCREENING LINE',
    detail: '2 LIGHT SIGNALS HOLD THE INNER RING',
    units: [
      {
        id: 'morrigan-screen-01',
        enemyClass: 'skirmisher',
        position: [-26, 0, -28],
        phaseOffset: 0.2,
      },
      {
        id: 'morrigan-screen-02',
        enemyClass: 'skirmisher',
        position: [32, 0, -18],
        phaseOffset: 1.4,
      },
    ],
  },
  {
    id: 'hunter-pincer',
    title: 'BREAK THE HUNTER PINCER',
    detail: 'REINFORCEMENTS ARE COLLAPSING ON YOUR VECTOR',
    units: [
      {
        id: 'morrigan-hunter-01',
        enemyClass: 'skirmisher',
        position: [-47, 0, 2],
        phaseOffset: 2.6,
      },
      {
        id: 'morrigan-hunter-02',
        enemyClass: 'skirmisher',
        position: [43, 0, -44],
        phaseOffset: 3.1,
      },
    ],
  },
  {
    id: 'command-break',
    title: 'ISOLATE THE COMMAND UNIT',
    detail: 'HEAVY SIGNATURE EXPOSED // FINISH THE SIEGE',
    units: [
      {
        id: 'morrigan-command',
        enemyClass: 'bulwark',
        position: [7, 0, -67],
        phaseOffset: 3.7,
      },
    ],
  },
]

export class MissionDirector {
  private stageIndex = 0
  private phase: MissionPhase = 'active'
  private intermissionRemaining = 0
  private defeatedEnemies = 0
  private readonly totalEnemies: number

  constructor(
    private readonly waves: readonly MissionWaveDefinition[] = DEFAULT_MISSION_WAVES,
    private readonly intermissionSeconds = 1.5,
  ) {
    if (waves.length === 0) throw new Error('MissionDirector requires at least one wave')
    if (intermissionSeconds < 0) throw new Error('Mission intermission cannot be negative')
    this.totalEnemies = waves.reduce((total, wave) => total + wave.units.length, 0)
    if (this.totalEnemies === 0) throw new Error('MissionDirector requires at least one enemy')
  }

  get currentWave(): MissionWaveDefinition {
    const wave = this.waves[this.stageIndex]
    if (!wave) throw new Error(`Mission wave ${this.stageIndex} is missing`)
    return wave
  }

  update(deltaSeconds: number, waveEnemiesAlive: number): MissionTransition {
    const transition: MissionTransition = {
      enteredIntermission: false,
      completed: false,
      spawnWave: null,
    }

    if (this.phase === 'complete') return transition

    if (this.phase === 'active' && waveEnemiesAlive <= 0) {
      this.defeatedEnemies += this.currentWave.units.length
      if (this.stageIndex === this.waves.length - 1) {
        this.phase = 'complete'
        transition.completed = true
      } else {
        this.phase = 'intermission'
        this.intermissionRemaining = this.intermissionSeconds
        transition.enteredIntermission = true
      }
      return transition
    }

    if (this.phase === 'intermission') {
      this.intermissionRemaining = Math.max(
        0,
        this.intermissionRemaining - Math.max(0, deltaSeconds),
      )
      if (this.intermissionRemaining <= 1e-6) {
        this.stageIndex += 1
        this.phase = 'active'
        this.intermissionRemaining = 0
        transition.spawnWave = this.currentWave
      }
    }

    return transition
  }

  snapshot(waveEnemiesAlive: number): MissionStateSnapshot {
    const currentWaveDefeated =
      this.phase === 'active'
        ? Math.max(0, this.currentWave.units.length - Math.max(0, waveEnemiesAlive))
        : 0
    const defeated = this.phase === 'complete' ? this.totalEnemies : this.defeatedEnemies + currentWaveDefeated
    return {
      phase: this.phase,
      stage: this.stageIndex + 1,
      totalStages: this.waves.length,
      waveEnemiesAlive: Math.max(0, waveEnemiesAlive),
      intermissionRemaining: Number(this.intermissionRemaining.toFixed(2)),
      progress: Math.min(1, defeated / this.totalEnemies),
    }
  }
}
